import { Chip, Spinner } from '@nextui-org/react'
import { AnimatePresence, motion } from 'framer-motion'
import moment from 'moment'
import { FC, ReactNode, useState } from 'react'
import { MdCancel, MdCheckCircle, MdChevronRight } from 'react-icons/md'
import { useSelector } from 'react-redux'
import { IConfigSeller } from '@/models/configSeller'
import { badgeStyle, REUSE_COLORS, withAlpha } from '@/utils/reuseBrand'
import { currencyFormat } from '@/utils/currencyFormat'
import { StatusColor } from '@/utils/StatusColor.utility'
import { hasInvalidImei } from '@/utils/canShowIAEvaluation'
import { brandLogo } from '@/utils/brandLogo'
import Checklist from './Checklist'
import Contract from './Contract'
import Discounts from './Discounts'
import IAEvaluation from './IAEvaluation'
import ImeiCorrection from './ImeiCorrection'
import PhonecheckCard from './PhonecheckCard'
import ReceiveCard from './ReceiveCard'
import SwitchBattery from './SwitchBattery'
import TradeInActionsToolbar from './TradeInActionsToolbar'
import { StepFooterActionContext } from '../context/StepFooterActionContext'
import { useTradeinContext } from '../hooks/useTradeinContext'
import { useTradeinFlags } from '../hooks/useTradeinFlags'

interface Step {
  key: string
  label: string
  applicable: boolean
  done: boolean
  render: () => ReactNode
  summary?: () => ReactNode
}

// Estados donde el equipo efectivamente se recibió físicamente (recepción exitosa):
// 5 Entregado, 7 Retirado Reuse, 9 Custodia, 11 En Bodega.
// (6 No Aprobado y 8 Rechazado NO cuentan aunque el flujo haya avanzado antes de rechazarse.)
// Lista explícita e independiente de STATUS_GROUPS.finalizados (@/utils/tradeinStatusGroups),
// que agrupa estados para KPIs de UI y puede cambiar por razones ajenas a esta semántica.
const RECEIVED_STATUS_IDS = [5, 7, 9, 11]

const StepFlowView: FC = () => {
  const { ctx } = useTradeinContext()
  const flags = useTradeinFlags()
  const configSeller = useSelector((store: { config: IConfigSeller }) => store.config)
  const idStatus = ctx.tradein.id_status
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const [footerAction, setFooterAction] = useState<ReactNode>(null)

  if (ctx.tradein.id === 0) {
    return (
      <div className='flex flex-col items-center justify-center gap-3 py-16'>
        <Spinner size='lg' />
        <span className='text-sm text-slate-400'>Cargando trade-in...</span>
      </div>
    )
  }

  const enableCheckBattery = (flags.isTelcelCorpMexico || flags.isTelcelMexico) ? 1 : configSeller.config.permisos.ENABLE_CHECK_BATTERY
  const isTablet = ctx.tradein.sbu.includes('Tablet')
  const showDiscounts = enableCheckBattery === 1 && !flags.isConsola && (
    flags.isSamsungPeru
      ? !flags.isSamsungRomPeru && flags.canEvaluateBatteryWithSwitch
      : flags.isSamsungRomPeru
        ? ctx.tradein.osiptel_evaluation !== null && ctx.tradein.osiptel_evaluation !== 1 && (flags.canEvaluateBatteryWithSwitch || isTablet)
        : flags.isMacstoreMexico
          ? !flags.isNovabench && idStatus === 4 && (flags.canEvaluateBatteryWithSwitch || flags.useDiscountsByBatteryModel || isTablet)
          /* Resto de sellers (Telcel incluido): desde Validado en adelante. Samsung Mexico es la
             excepcion, ahi el paso aparece antes y solo para equipos que no son Smartwatch. */
          : !flags.isNovabench && flags.canEvaluateBatteryWithSwitch && (idStatus >= 3 || (flags.isSamsungMexico && ctx.tradein.sbu !== 'Smartwatch'))
  )

  // Smartphone con IMEI inválido: el paso de prueba queda abierto hasta que lo ingresen a mano.
  const needsImei = hasInvalidImei(ctx.tradein) && ctx.tradein.grade_IA === null && idStatus !== 8

  const steps: Step[] = [
    {
      key: 'test',
      label: flags.hasPhonecheck ? 'Prueba Phonecheck' : flags.isNovabench ? 'Prueba Novabench' : 'Checklist técnico',
      applicable: true,
      done: ![1, 6].includes(idStatus) && !needsImei,
      render: () => {
        if (needsImei) return <ImeiCorrection />
        if (flags.hasPhonecheck) return <PhonecheckCard />
        if (flags.isNovabench) {
          return (
            <div className='bg-white rounded-xl border border-slate-100 shadow-sm p-4 text-sm text-slate-500'>
              La revisión Novabench para notebooks está disponible en la ficha completa del trade-in.
            </div>
          )
        }
        return <Checklist />
      },
      summary: () => {
        if (flags.isNovabench) return <span>Revisado por Novabench.</span>
        if (flags.hasPhonecheck) return <span>Prueba Phonecheck superada.</span>
        return <span>Checklist técnico completado.</span>
      }
    },
    {
      key: 'estetica',
      label: 'Evaluación estética',
      applicable: flags.hasIAEvaluation,
      done: ctx.tradein.grade_IA !== null,
      render: () => <IAEvaluation showSwitchBattery={flags.showSwitchBattery} useDiscountsByBatteryModel={flags.useDiscountsByBatteryModel} />,
      summary: () => <span>Categoría estética: <strong>{ctx.tradein.grade_IA}</strong></span>
    },
    {
      key: 'bateria',
      label: 'Estado de batería',
      applicable: flags.showSwitchBattery === true || showDiscounts,
      done: ctx.tradein.isBatteryHealthy !== null,
      render: () => (flags.showSwitchBattery === true ? <SwitchBattery /> : <Discounts />),
      summary: () => <span>Batería: <strong>{ctx.tradein.isBatteryHealthy === 1 ? 'Sobre 80%' : 'Bajo 80%'}</strong></span>
    },
    {
      key: 'contrato',
      label: 'Contrato',
      applicable: ctx.displayContract === 1,
      // El contrato generado (docusign_id) queda "esperando firma" mientras id_status siga en
      // Validado(4) — recién al firmar pasa a Firmado(3) o estados posteriores. Si se marcara done
      // solo con docusign_id, el stepper saltaría directo a Recepción antes de que el cliente firme,
      // ocultando el botón «Firmar Contrato Aqui» de Contract.tsx.
      done: Boolean(ctx.tradein.docusign_id) && ctx.tradein.id_status !== 4,
      render: () => <Contract showSwitchBattery={flags.showSwitchBattery} />,
      summary: () => <span>N° contrato: <strong>{ctx.tradein.docusign_id}</strong>{ctx.tradein.contract_email ? <> · enviado a {ctx.tradein.contract_email}</> : null}</span>
    },
    {
      key: 'recepcion',
      label: 'Recepción',
      applicable: true,
      done: RECEIVED_STATUS_IDS.includes(idStatus),
      render: () => <ReceiveCard />,
      summary: () => <span>Equipo recibido correctamente.</span>
    }
  ]

  const applicableSteps = steps.filter(s => s.applicable)
  const currentStep = applicableSteps.find(s => !s.done)
  // No Aprobado / Rechazado: el flujo ya no continúa, así que el paso "actual" en realidad
  // es el que quedó fallido (se marca con una X roja en vez del círculo vacío + pulso).
  const isRejected = [6, 8].includes(idStatus)
  const doneCount = applicableSteps.filter(s => s.done).length
  const progressPercent = applicableSteps.length === 0 ? 100 : Math.round((doneCount / applicableSteps.length) * 100)

  const wasRecalculated = ctx.tradein.recalculate === '1'
  const brandChanged = wasRecalculated && ctx.tradein.brand !== ctx.tradein.make
  const modelChanged = wasRecalculated && ctx.tradein.model !== ctx.tradein.modelName
  // Marca/modelo "finales": el recalculado si difiere del original, si no el original tal cual.
  const displayBrand = brandChanged ? ctx.tradein.make : ctx.tradein.brand
  const displayModel = modelChanged ? ctx.tradein.modelName : ctx.tradein.model
  const logo = brandLogo(displayBrand ?? '')
  const renderRecalculable = (original: string, recalculated: string, changed: boolean) => (
    <>
      <span className={changed ? 'line-through text-slate-400' : ''}>{original}</span>
      {changed && <strong className='ml-1 text-slate-700'>{recalculated}</strong>}
    </>
  )

  return (
    <div className='flex h-full flex-col gap-4'>
      <div className='flex items-start justify-between gap-2 pr-8'>
        <div className='flex flex-col gap-1 min-w-0'>
          <div className='flex items-center gap-2 flex-wrap min-w-0'>
            <span className='text-tiny font-bold uppercase tracking-wide rounded-md px-1.5 py-0.5 shrink-0' style={badgeStyle(REUSE_COLORS.reBlue, 0.14)}>Trade-in #{ctx.tradein.id}</span>
            <span className='text-sm font-semibold text-slate-700 truncate'>
              {renderRecalculable(ctx.tradein.brand, ctx.tradein.make, brandChanged)} {renderRecalculable(ctx.tradein.model, ctx.tradein.modelName, modelChanged)}
              {ctx.tradein.capacity ? <> · {renderRecalculable(ctx.tradein.capacity, ctx.tradein.memory, wasRecalculated && ctx.tradein.capacity !== ctx.tradein.memory)}</> : null}
            </span>
          </div>
          <div className='flex flex-wrap items-center gap-x-4 gap-y-1 text-tiny text-slate-500'>
            <span>
              Monto de descuento:{' '}
              <strong className={ctx.tradein.new_discount_amount !== null ? 'line-through text-slate-400' : 'text-slate-700'}>
                {currencyFormat(ctx.tradein.discount_amount, import.meta.env.VITE_COUNTRY)}
              </strong>
              {ctx.tradein.new_discount_amount !== null && (
                <strong className='text-slate-700 ml-1'>{currencyFormat(ctx.tradein.new_discount_amount, import.meta.env.VITE_COUNTRY)}</strong>
              )}
            </span>
            <span>Fecha de creación: <strong className='text-slate-700'>{moment(ctx.tradein.created_at).format('DD-MM-YYYY HH:mm')}</strong></span>
            {ctx.tradein.serie && <span>Serie/IMEI: <strong className='text-slate-700'>{ctx.tradein.serie}</strong></span>}
          </div>
        </div>
        {/* Chip de estado y logo de marca comparten esta columna alineada a la derecha, así el
            logo siempre queda anclado al mismo borde que el estado, sin importar el aspect ratio
            de cada wordmark (Samsung/Xiaomi son mucho más anchos que Apple/Motorola) — max-w +
            object-contain evita que un logo ancho se desborde del modal. */}
        <div className='flex flex-col items-end gap-1.5 shrink-0'>
          <Chip size='sm' radius='md' style={badgeStyle(StatusColor(ctx.tradein.status, true), 0.16)}>{ctx.tradein.status}</Chip>
          {logo && <img src={logo} alt={displayBrand} className='h-5 max-w-[110px] w-auto object-contain' />}
        </div>
      </div>

      {/* Barra de avance general: cuenta solo los pasos aplicables a este tradein específico. */}
      <div className='flex items-center gap-3'>
        <div className='h-1.5 flex-1 overflow-hidden rounded-full bg-slate-100'>
          <motion.div
            className='h-full rounded-full'
            style={{ background: `linear-gradient(90deg, ${REUSE_COLORS.reBlue}, ${REUSE_COLORS.solidPurple})` }}
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          />
        </div>
        <span className='text-tiny font-semibold text-slate-400 whitespace-nowrap'>{doneCount}/{applicableSteps.length} pasos</span>
      </div>

      <div className='flex flex-col md:flex-row gap-4'>
        <div className='md:w-1/3'>
          <p className='text-tiny font-semibold uppercase tracking-wide text-slate-400 mb-2'>Resumen</p>
          <div className='relative rounded-xl border border-slate-200 p-3'>
            {/* Línea vertical del timeline, atravesando el centro de cada nodo. */}
            <div className='absolute left-[26px] top-6 bottom-6 w-0.5 bg-slate-200' />
            <div className='flex flex-col'>
              {applicableSteps.map((step, index) => {
                const isCurrent = step === currentStep
                const isExpanded = expandedKey === step.key
                const canExpand = step.done && step.summary !== undefined
                const isFailed = isCurrent && isRejected && !step.done
                return (
                  <motion.div
                    key={step.key}
                    initial={{ opacity: 0, x: -6 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.2, delay: index * 0.04 }}
                    className='relative'
                  >
                    <button
                      type='button'
                      disabled={!canExpand}
                      onClick={() => setExpandedKey(isExpanded ? null : step.key)}
                      className={['flex w-full items-start gap-3 py-2 text-left', canExpand ? 'cursor-pointer' : 'cursor-default'].join(' ')}
                    >
                      <span
                        className='relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 bg-white'
                        style={{
                          borderColor: step.done ? REUSE_COLORS.solidPurple : isFailed ? '#ef4444' : isCurrent ? REUSE_COLORS.solidPurple : '#e2e8f0',
                          backgroundColor: step.done ? REUSE_COLORS.solidPurple : '#ffffff'
                        }}
                      >
                        <AnimatePresence mode='wait' initial={false}>
                          {step.done
                            ? (
                              <motion.span
                                key='done'
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                                className='flex items-center justify-center'
                              >
                                <MdCheckCircle className='text-base text-white' />
                              </motion.span>
                              )
                            : isFailed
                              ? (
                                <motion.span
                                  key='failed'
                                  initial={{ scale: 0, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  transition={{ type: 'spring', stiffness: 500, damping: 20 }}
                                  className='flex items-center justify-center'
                                >
                                  <MdCancel className='text-base text-red-500' />
                                </motion.span>
                                )
                              : (
                                <motion.span
                                  key='pending'
                                  initial={{ scale: 0.6, opacity: 0 }}
                                  animate={{ scale: 1, opacity: 1 }}
                                  className={['h-2 w-2 rounded-full', isCurrent ? '' : 'bg-slate-200'].join(' ')}
                                  style={isCurrent ? { backgroundColor: REUSE_COLORS.solidPurple } : undefined}
                                />
                                )}
                        </AnimatePresence>
                        {isCurrent && !step.done && !isRejected && (
                          <motion.span
                            className='absolute inset-0 rounded-full border-2'
                            style={{ borderColor: REUSE_COLORS.solidPurple }}
                            animate={{ scale: [1, 1, 1.9], opacity: [0, 0.6, 0] }}
                            transition={{ duration: 1.3, repeat: Infinity, ease: 'easeOut', times: [0, 0.15, 1] }}
                          />
                        )}
                      </span>
                      <span className='flex-1 min-w-0 pt-1'>
                        <span className={['block text-sm', isFailed ? 'text-red-500 font-medium' : step.done || isCurrent ? 'text-slate-700 font-medium' : 'text-slate-300'].join(' ')}>{step.label}</span>
                      </span>
                      {canExpand && (
                        <motion.span animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.15 }} className='shrink-0 text-slate-400 flex mt-1.5'>
                          <MdChevronRight />
                        </motion.span>
                      )}
                    </button>
                    <AnimatePresence initial={false}>
                      {isExpanded && step.summary && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.18 }}
                          className='overflow-hidden'
                        >
                          <div className='ml-10 mb-2 rounded-lg bg-slate-50 px-2.5 py-2 text-tiny text-slate-600'>
                            {step.summary()}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </div>
          </div>
        </div>
        <div className='md:w-2/3'>
          <p className='text-tiny font-semibold uppercase tracking-wide text-slate-400 mb-2'>Etapa actual</p>
          <AnimatePresence mode='wait'>
            {isRejected
              // El rechazo (temprano o tardío) tiene prioridad sobre cualquier otro estado: aunque
              // currentStep exista (ej. 'recepcion' quedó pendiente porque nunca se recibió el equipo),
              // no tiene sentido pedirle al usuario que complete ese paso — el flujo ya terminó en rechazo.
              ? (
                <motion.div
                  key='rejected'
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className='flex flex-col items-center justify-center gap-3 rounded-xl border py-12 px-6 text-center'
                  style={{ backgroundColor: withAlpha('#ef4444', 0.12), borderColor: withAlpha('#ef4444', 0.3) }}
                >
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 260, damping: 16, delay: 0.1 }}
                    className='flex h-16 w-16 items-center justify-center rounded-full bg-[#ef4444]'
                  >
                    <motion.span
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={{ pathLength: 1, opacity: 1 }}
                      transition={{ duration: 0.35, delay: 0.3 }}
                    >
                      <MdCancel className='text-4xl text-white' />
                    </motion.span>
                  </motion.span>
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35, duration: 0.25 }}
                  >
                    <p className='text-xl font-black text-slate-800'>Rechazado</p>
                    <p className='text-sm text-slate-500 mt-1'>
                      El trade-in de <strong className='text-slate-700'>{displayBrand} {displayModel}</strong> fue rechazado.
                    </p>
                    {ctx.tradein.cancel_reason && (
                      <p className='text-sm text-slate-500 mt-1'>
                        Motivo: <strong className='text-slate-700'>{ctx.tradein.cancel_reason}</strong>
                      </p>
                    )}
                  </motion.div>
                </motion.div>
                )
              : currentStep
                ? (
                  <motion.div
                    key={currentStep.key}
                    initial={{ opacity: 0, x: 12 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -12 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  >
                    <StepFooterActionContext.Provider value={setFooterAction}>
                      {currentStep.render()}
                    </StepFooterActionContext.Provider>
                  </motion.div>
                  )
                : (
                  <motion.div
                    key='done-all'
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className='flex flex-col items-center justify-center gap-3 rounded-xl border py-12 px-6 text-center'
                    style={{ backgroundColor: withAlpha(REUSE_COLORS.fizzyGreen, 0.12), borderColor: withAlpha(REUSE_COLORS.fizzyGreen, 0.3) }}
                  >
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 260, damping: 16, delay: 0.1 }}
                      className='flex h-16 w-16 items-center justify-center rounded-full bg-[#22c55e]'
                    >
                      <motion.span
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{ duration: 0.35, delay: 0.3 }}
                      >
                        <MdCheckCircle className='text-4xl text-white' />
                      </motion.span>
                    </motion.span>
                    <motion.div
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.35, duration: 0.25 }}
                    >
                      <p className='text-xl font-black text-slate-800'>Excelente</p>
                      <p className='text-sm text-slate-500 mt-1'>
                        El trade-in de <strong className='text-slate-700'>{displayBrand} {displayModel}</strong> completó todas sus etapas con éxito.
                      </p>
                    </motion.div>
                  </motion.div>
                  )}
          </AnimatePresence>
        </div>
      </div>

      {/* Footer de acciones: Rechazar siempre a la izquierda, el resto a la derecha (acciones
          administrativas + la acción principal del paso actual, registrada vía StepFooterActionContext).
          mt-auto lo deja siempre pegado abajo del modal, sin importar cuánto contenido haya arriba. */}
      <div className='mt-auto flex items-center justify-between gap-2 flex-wrap border-t border-slate-100 pt-3'>
        <TradeInActionsToolbar variant='danger' />
        <div className='flex flex-wrap items-center justify-end gap-1.5'>
          <TradeInActionsToolbar variant='other' />
          {footerAction}
        </div>
      </div>
    </div>
  )
}

export default StepFlowView
